/** Canonical Add Node catalog — every creatable node type exposed in
 * Canvas "Add Node" surfaces (context menu, left library, Quick Add).
 *
 * VISTOERA_PLATFORM_ADMIN_CANVAS_NODE_CATALOG_ICON_ORDER_AVAILABILITY_
 * AND_VISIBILITY_FOUNDATION_V2: visibility is the intersection of
 * (1) local LEGACY_HIDDEN filter and (2) Platform Node Catalog
 * ONLINE+READY tenant view (session cache). Backend also rejects
 * NODE_OFFLINE on create — frontend hide is not the only gate.
 */

import { getNodeDefinition, NODE_DEFINITIONS, nodesByCategory, type NodeDefinition } from './nodeRegistry'
import { isNodeCreatableInCatalog } from './nodeCatalogRuntime'
import type { CanvasNodeCategory } from './types'

/** VISTOERA_CANVAS_PROJECT_START_TO_GENERATE_CREATIVE_UNIFIED_
 * GENERATION_NODE_V2 §2 — `project_start`/`creative_candidate` stay
 * registered (so already-persisted Canvas JSON keeps loading — see
 * `canvasNodeTypes.ts`/`canvasRenderContract.ts`) but must never be
 * user-CREATABLE again: every "Add Node" surface (context menu, left
 * rail, Quick Add) now shows ONLY the unified `generate_creative` type
 * (spec §2 "不要再把 Project Start / 项目发起 作为用户侧一级 Canvas 节点
 * 概念展示"). `toolbarOrder.ts` imports this SAME set so a legacy id
 * persisted in an old user's `localStorage` order also gets dropped,
 * never re-surfacing the old left-rail entry.
 *
 * VISTOERA_SCENE_SHOT_CONTAINER_ORIGIN_PRODUCT_ROLE_AND_CONTEXT_MENU_
 * EXPOSURE_AUDIT_P0_V1 — `scene` (UI: "Scene / Shot 容器") is a
 * Production Canvas V1 visual grouping frame (parentId / childCount /
 * resize only). It is NOT Storyboard materialization, NOT video
 * composition, and MUST NOT appear in tenant Add Node surfaces.
 * Registered render + persistence stay for backward-compatible load. */
export const LEGACY_HIDDEN_FROM_ADD_NODE_CATALOG: ReadonlySet<string> = new Set([
  'project_start',
  'creative_candidate',
  'scene',
])

export const ADD_NODE_CATALOG_NODE_TYPES: readonly string[] = NODE_DEFINITIONS.map((d) => d.nodeType).filter(
  (t) => !LEGACY_HIDDEN_FROM_ADD_NODE_CATALOG.has(t),
)

export function getAddNodeCatalogDefinitions(): readonly NodeDefinition[] {
  return ADD_NODE_CATALOG_NODE_TYPES.filter((t) => isNodeCreatableInCatalog(t)).map((t) => getNodeDefinition(t))
}

export function isVisibleInAddNodeCatalog(nodeType: string): boolean {
  return ADD_NODE_CATALOG_NODE_TYPES.includes(nodeType) && isNodeCreatableInCatalog(nodeType)
}

export function addNodeCatalogByCategory(): Array<{ category: CanvasNodeCategory; items: NodeDefinition[] }> {
  return nodesByCategory().map((group) => ({
    ...group,
    items: group.items.filter(
      (d) => !LEGACY_HIDDEN_FROM_ADD_NODE_CATALOG.has(d.nodeType) && isNodeCreatableInCatalog(d.nodeType),
    ),
  }))
}
